import { on } from '@ngrx/store';

import { CreateTransactionMutation } from 'src/generated/graphql.generated';

import { CreateTransactionDialogActions } from './create-transaction-dialog.actions';
import {
    CreateTransactionDialogState,
    CreateTransactionTarget,
    createTransactionDialogInitialState,
} from './create-transaction-dialog.state';
import {
    CARD_PAYMENT_METHODS,
    previousStep,
    resolvePaymentTypeForMethod,
} from './create-transaction-dialog.util';

const settleNewInvoice = (
    state: CreateTransactionDialogState,
    { createTransaction }: CreateTransactionMutation,
): CreateTransactionDialogState => {
    if (state.target?.kind !== 'newInvoice') { return state; }

    return createTransaction.newInvoice
        ? { ...state, raisedInvoiceId: createTransaction.newInvoice.id }
        : { ...state, target: { kind: 'deposit' } };
};

const openDialog = (
    previous: CreateTransactionDialogState,
    { jobId, customerId, currency }: {
        jobId: string;
        customerId: string;
        currency: string;
    },
    preselected?: { target?: CreateTransactionTarget; amountMinor?: number },
): CreateTransactionDialogState => ({
    ...createTransactionDialogInitialState,
    session: previous.session + 1,
    open: true,
    step: preselected?.target ? 'method' : 'targets',
    jobId,
    customerId,
    currency,
    target: preselected?.target,
    amountMinor: preselected?.amountMinor
        ?? createTransactionDialogInitialState.amountMinor,
});

export const createTransactionDialogReducers = [

    on(
        CreateTransactionDialogActions.createTransactionButtonClicked,
        CreateTransactionDialogActions.dispatchCardCreateTransactionButtonClicked,
        (
            state: CreateTransactionDialogState,
            action,
        ): CreateTransactionDialogState => openDialog(state, action),
    ),

    on(
        CreateTransactionDialogActions.invoicePayButtonClicked,
        (
            state: CreateTransactionDialogState,
            action,
        ): CreateTransactionDialogState => openDialog(state, action, {
            target: { kind: 'invoice', invoiceId: action.invoiceId },
            amountMinor: action.amountMinor,
        }),
    ),

    on(
        CreateTransactionDialogActions.workOrderCreateTransactionButtonClicked,
        (
            state: CreateTransactionDialogState,
            action,
        ): CreateTransactionDialogState => openDialog(state, action, {
            target: action.invoiceId
                ? { kind: 'invoice', invoiceId: action.invoiceId }
                : { kind: 'deposit' },
            amountMinor: action.amountMinor,
        }),
    ),

    // Requirement: this button answers a deposit-required booking warning, so
    // it always creates a deposit. Pay Event uses the work-order action above
    // and targets the event's open invoice when one exists.
    on(
        CreateTransactionDialogActions.bookingPayButtonClicked,
        (
            state: CreateTransactionDialogState,
            action,
        ): CreateTransactionDialogState => openDialog(state, action, {
            target: { kind: 'deposit' },
            amountMinor: action.amountMinor,
        }),
    ),

    on(
        CreateTransactionDialogActions.preselectedTargetUnavailable,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            step: 'targets',
            target: undefined,
            amountMinor: createTransactionDialogInitialState.amountMinor,
        }),
    ),

    // Requirement: a job already carrying authorizations is asked about those
    // FIRST. The operator who opens this dialog on such a job almost always
    // means to settle one, and collecting something new over the top of a hold
    // leaves the customer's money frozen for the issuer to expire.
    on(
        CreateTransactionDialogActions.openHoldsFoundOnJob,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            step: 'holds',
        }),
    ),

    // Requirement: completing a hold uses the HOLD's amount and the TILE's id,
    // not the wizard's own in-progress figure — a holds list may show several.
    on(
        CreateTransactionDialogActions.holdTileClicked,
        (
            state: CreateTransactionDialogState,
            { transactionId, amountMinor },
        ): CreateTransactionDialogState => ({
            ...state,
            step: 'complete',
            openPreauthTransactionId: transactionId,
            amountMinor,
        }),
    ),

    on(
        CreateTransactionDialogActions.createNewTransactionButtonClicked,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            step: 'targets',
        }),
    ),

    on(
        CreateTransactionDialogActions.targetInferredFromJob,
        (
            state: CreateTransactionDialogState,
            { target },
        ): CreateTransactionDialogState => ({ ...state, target, step: 'amount' }),
    ),

    on(
        CreateTransactionDialogActions.depositPreselected,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            target: { kind: 'deposit' },
        }),
    ),

    on(
        CreateTransactionDialogActions.newInvoicePreselected,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            target: { kind: 'newInvoice' },
        }),
    ),

    on(
        CreateTransactionDialogActions.targetTileClicked,
        (
            state: CreateTransactionDialogState,
            { target },
        ): CreateTransactionDialogState => ({
            ...state,
            target,
            step: 'amount',
            raisedInvoiceId: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.newInvoiceTileClicked,
        (
            state: CreateTransactionDialogState,
            { eventIds, askForEvents },
        ): CreateTransactionDialogState => ({
            ...state,
            target: { kind: 'newInvoice' },
            newInvoiceEventIds: eventIds,
            askedForInvoiceEvents: askForEvents,
            raisedInvoiceId: undefined,
            step: askForEvents ? 'events' : 'amount',
        }),
    ),

    on(
        CreateTransactionDialogActions.invoiceEventTileClicked,
        (
            state: CreateTransactionDialogState,
            { eventId, singleSelect },
        ): CreateTransactionDialogState => {
            if (singleSelect) { return { ...state, newInvoiceEventIds: [ eventId ] }; }

            const chosen = state.newInvoiceEventIds.includes(eventId);

            return {
                ...state,
                newInvoiceEventIds: chosen
                    ? state.newInvoiceEventIds.filter((id) => id !== eventId)
                    : [ ...state.newInvoiceEventIds, eventId ],
            };
        },
    ),

    on(
        CreateTransactionDialogActions.continueToAmountButtonClicked,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            step: 'amount',
        }),
    ),

    on(
        CreateTransactionDialogActions.amountSeededFromTargets,
        CreateTransactionDialogActions.amountChipClicked,
        (
            state: CreateTransactionDialogState,
            { amountMinor },
        ): CreateTransactionDialogState => ({
            ...state,
            amountMinor,
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.amountChanged,
        CreateTransactionDialogActions.completeAmountSeededFromBalance,
        CreateTransactionDialogActions.completeAmountChanged,
        (
            state: CreateTransactionDialogState,
            { amountMinor },
        ): CreateTransactionDialogState => ({
            ...state,
            amountMinor,
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.continueToMethodButtonClicked,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            step: 'method',
        }),
    ),

    on(
        CreateTransactionDialogActions.methodTileClicked,
        (
            state: CreateTransactionDialogState,
            { method },
        ): CreateTransactionDialogState => ({
            ...state,
            method,
            step: 'details',
            selectedPaymentTypeId: resolvePaymentTypeForMethod(
                state.paymentTypes, method, '',
            )?.id,
            // Requirement: a sale is the default. A hold chosen for one method
            // is never carried into another.
            holdInsteadOfCharge: false,
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.paymentTypeTileClicked,
        (
            state: CreateTransactionDialogState,
            { paymentTypeId },
        ): CreateTransactionDialogState => ({
            ...state,
            method: 'other',
            step: 'details',
            selectedPaymentTypeId: paymentTypeId,
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.collectingValueClicked,
        (
            state: CreateTransactionDialogState,
            { step },
        ): CreateTransactionDialogState => ({ ...state, step, error: undefined }),
    ),

    on(
        CreateTransactionDialogActions.backButtonClicked,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => {
            if (state.step === 'details' && state.method && CARD_PAYMENT_METHODS.includes(state.method)) {
                return {
                    ...state,
                    method: 'recordCard',
                    selectedPaymentTypeId: undefined,
                    error: undefined,
                };
            }

            return {
                ...state,
                step: previousStep(state.step, state.target, state.askedForInvoiceEvents),
                error: undefined,
            };
        },
    ),

    on(
        CreateTransactionDialogActions.remarksChanged,
        (
            state: CreateTransactionDialogState,
            { remarks },
        ): CreateTransactionDialogState => ({ ...state, remarks }),
    ),

    on(
        CreateTransactionDialogActions.sendReceiptToggled,
        (
            state: CreateTransactionDialogState,
            { sendReceipt },
        ): CreateTransactionDialogState => ({ ...state, sendReceipt }),
    ),

    on(
        CreateTransactionDialogActions.loadPaymentTypeSuccess,
        (
            state: CreateTransactionDialogState,
            { paymentTypeId, differentialPct, differentialPctKnown, holdsEnabled },
        ): CreateTransactionDialogState => ({
            ...state,
            paymentTypeId,
            differentialPct,
            differentialPctKnown,
            holdsEnabled,
        }),
    ),

    on(
        CreateTransactionDialogActions.loadPaymentTypesSuccess,
        (
            state: CreateTransactionDialogState,
            { paymentTypes },
        ): CreateTransactionDialogState => ({
            ...state,
            paymentTypes,
            paymentTypesFailed: false,
            paymentTypesLoaded: true,
            selectedPaymentTypeId: state.method
                ? resolvePaymentTypeForMethod(paymentTypes, state.method, '')?.id
                    ?? state.selectedPaymentTypeId
                : state.selectedPaymentTypeId,
        }),
    ),

    // Requirement: a failed payment-types read must not read as "zone has none
    // configured" — that claim about configuration cannot be made from a
    // dropped request.
    on(
        CreateTransactionDialogActions.loadPaymentTypesError,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            paymentTypesFailed: true,
        }),
    ),

    on(
        CreateTransactionDialogActions.keyInCardButtonClicked,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            creating: true,
            openingCardForm: true,
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.cardFormOpened,
        (
            state: CreateTransactionDialogState,
            { embedUrl },
        ): CreateTransactionDialogState => ({
            ...state,
            embedUrl,
            creating: false,
            openingCardForm: false,
            // Requirement: cardToken is single-use. A fresh form clears it so a
            // stale token is never charged.
            cardToken: undefined,
            cardLast4: undefined,
            selectedCard: state.selectedCard?.kind === 'new'
                ? undefined
                : state.selectedCard,
            // Requirement: the save-card answer is per card. A new form resets
            // it to the default so an answer given for the last card is never
            // silently applied to this one.
            saveCard: createTransactionDialogInitialState.saveCard,
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.cardFormClosed,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            embedUrl: undefined,
            cardToken: undefined,
            cardLast4: undefined,
            selectedCard: state.selectedCard?.kind === 'new'
                ? undefined
                : state.selectedCard,
        }),
    ),

    on(
        CreateTransactionDialogActions.cardTokenReceived,
        (
            state: CreateTransactionDialogState,
            { cardToken, last4 },
        ): CreateTransactionDialogState => ({
            ...state,
            cardToken,
            cardLast4: last4 ?? undefined,
            embedUrl: undefined,
            selectedCard: { kind: 'new' },
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.saveCardToggled,
        (
            state: CreateTransactionDialogState,
            { saveCard },
        ): CreateTransactionDialogState => ({ ...state, saveCard }),
    ),

    on(
        CreateTransactionDialogActions.vaultOnPayLinkToggled,
        (
            state: CreateTransactionDialogState,
            { vaultOnPayLink },
        ): CreateTransactionDialogState => ({ ...state, vaultOnPayLink }),
    ),

    on(
        CreateTransactionDialogActions.holdInsteadOfChargeChosen,
        (
            state: CreateTransactionDialogState,
            { hold },
        ): CreateTransactionDialogState => ({
            ...state,
            holdInsteadOfCharge: hold && state.holdsEnabled,
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.captureHoldConfirmed,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            creating: true,
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.cardsOnFileLoaded,
        (
            state: CreateTransactionDialogState,
            { cardsOnFile },
        ): CreateTransactionDialogState => {
            const selected = state.selectedCard;

            // Requirement: a card removed from cardsOnFile cannot stay selected —
            // charging it would earn BlueSage's `payment_profile_not_found` with
            // the customer waiting. A keyed-in card is never in this list, so it
            // is never pruned by it.
            const gone = selected?.kind === 'onFile'
                && !cardsOnFile.some((card) => card.token === selected.token);

            return { ...state, cardsOnFile, selectedCard: gone ? undefined : selected };
        },
    ),

    on(
        CreateTransactionDialogActions.cardOnFileSelected,
        (
            state: CreateTransactionDialogState,
            { token },
        ): CreateTransactionDialogState => ({
            ...state,
            selectedCard: { kind: 'onFile', token },
            // Requirement: selecting a card on file does not clear `cardToken`.
            // Clearing it here would destroy a still-valid keyed-in token,
            // forcing the operator to re-key the card in front of the customer.
            error: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.newCardSelected,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            selectedCard: { kind: 'new' },
            error: undefined,
        }),
    ),

    // Requirement: this discards an unstored token, not a saved card — nothing
    // is stored yet, so there is nothing to call; the one-time token simply
    // expires unused at BlueSage.
    on(
        CreateTransactionDialogActions.discardNewCardClicked,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            cardToken: undefined,
            cardLast4: undefined,
            // Requirement: the save-card answer is per card, and reverts to the
            // default here too — carrying a tick forward would store a card
            // nobody consented to, and carrying a decline forward would lose one.
            saveCard: createTransactionDialogInitialState.saveCard,
            selectedCard: state.selectedCard?.kind === 'new'
                ? undefined
                : state.selectedCard,
        }),
    ),

    on(
        CreateTransactionDialogActions.deleteCardOnFileSuccess,
        (
            state: CreateTransactionDialogState,
            { token },
        ): CreateTransactionDialogState => ({
            ...state,
            cardsOnFile: state.cardsOnFile.filter((c) => c.token !== token),
            selectedCard: state.selectedCard?.kind === 'onFile'
                && state.selectedCard.token === token
                ? undefined
                : state.selectedCard,
        }),
    ),

    on(
        CreateTransactionDialogActions.sendToTerminalButtonClicked,
        CreateTransactionDialogActions.sendPayLinkButtonClicked,
        // Requirement: this clears `payUrl` but not `payLinkTransactionId` — the
        // resend effect reads that id off state, and effects run after reducers.
        CreateTransactionDialogActions.sendNewPayLinkButtonClicked,
        CreateTransactionDialogActions.recordPaymentButtonClicked,
        CreateTransactionDialogActions.chargeCardButtonClicked,
        CreateTransactionDialogActions.sendSquareCheckoutButtonClicked,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            creating: true,
            error: undefined,
            // Requirement: the previous pay link is cleared the moment a new one
            // is requested. A stale link left on screen could be read out to the
            // customer for the wrong amount or transaction.
            payUrl: undefined,
            // Requirement: createdTransactionId must not carry into a new
            // attempt — a failure asks the server to cancel whatever row it
            // names, and that must be this attempt's row.
            createdTransactionId: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.createTransactionSuccess,
        (
            state: CreateTransactionDialogState,
            { transactionId, data },
        ): CreateTransactionDialogState => ({
            ...settleNewInvoice(state, data),
            creating: false,
            // Requirement: createdTransactionId is held only until the push
            // confirms — if the device refuses the charge, this is the row that
            // must be closed.
            createdTransactionId: transactionId,
        }),
    ),

    on(
        CreateTransactionDialogActions.chargeTransactionCreated,
        (
            state: CreateTransactionDialogState,
            { transactionId, data },
        ): CreateTransactionDialogState => ({
            ...settleNewInvoice(state, data),
            createdTransactionId: transactionId,
        }),
    ),

    on(
        CreateTransactionDialogActions.unsentTransactionCancelled,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            createdTransactionId: undefined,
        }),
    ),

    on(
        CreateTransactionDialogActions.createTransactionError,
        CreateTransactionDialogActions.chargeCardError,
        CreateTransactionDialogActions.cardFormError,
        CreateTransactionDialogActions.captureHoldError,
        (
            state: CreateTransactionDialogState,
            { error },
        ): CreateTransactionDialogState => ({
            ...state,
            creating: false,
            openingCardForm: false,
            error: error?.message,
        }),
    ),

    on(
        CreateTransactionDialogActions.recordPaymentSuccess,
        CreateTransactionDialogActions.chargeCardSuccess,
        CreateTransactionDialogActions.cardHeldSuccess,
        CreateTransactionDialogActions.squareCheckoutLinkSent,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            creating: false,
            // Requirement: once payment succeeds, createdTransactionId is
            // cleared so a later failure elsewhere can never cancel this row,
            // and cardToken is cleared because it is now spent.
            createdTransactionId: undefined,
            cardToken: undefined,
            cardLast4: undefined,
            selectedCard: undefined,
        }),
    ),

    // Requirement: a pay link is not money received — the customer has not paid
    // yet — so this stays on its own screen with the URL to read out.
    // `payLinkResent` reuses it because a replacement link is the same fact as
    // a first one.
    on(
        CreateTransactionDialogActions.payLinkCreated,
        CreateTransactionDialogActions.payLinkResent,
        (
            state: CreateTransactionDialogState,
            { payUrl, transactionId },
        ): CreateTransactionDialogState => ({
            ...state,
            creating: false,
            payUrl,
            payLinkTransactionId: transactionId,
        }),
    ),

    on(
        CreateTransactionDialogActions.completeAuthorizationFromRowClicked,
        (
            state: CreateTransactionDialogState,
            action,
        ): CreateTransactionDialogState => ({
            ...openDialog(state, action),
            step: 'complete',
            openPreauthTransactionId: action.openPreauthTransactionId,
            // Requirement: completing for more than was authorized is a new
            // authorization, not a completion — so this uses the hold's own
            // amount, not whatever the wizard had pre-filled.
            amountMinor: action.amountMinor,
        }),
    ),

    on(
        CreateTransactionDialogActions.completeCancelled,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            step: 'holds',
        }),
    ),

    on(
        CreateTransactionDialogActions.jobLoadError,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...state,
            jobLoadFailed: true,
        }),
    ),

    // Requirement: session survives the close — resetting it would hand the
    // next opening a number a create still in flight is holding, the exact
    // collision the counter exists to prevent.
    on(
        CreateTransactionDialogActions.doneButtonClicked,
        CreateTransactionDialogActions.dialogClosed,
        (state: CreateTransactionDialogState): CreateTransactionDialogState => ({
            ...createTransactionDialogInitialState,
            session: state.session,
        }),
    ),
];
